/** Bottom → top stacking (OpenLayers adds later = drawn above). */
export type MapStackSlot =
  | { kind: 'project'; layerId: number }
  | { kind: 'localGeotiff'; localId: string }

export function slotToKey(slot: MapStackSlot): string {
  return slot.kind === 'project' ? `p:${slot.layerId}` : `g:${slot.localId}`
}

export function keyToSlot(key: string): MapStackSlot {
  if (key.startsWith('p:')) {
    return { kind: 'project', layerId: Number(key.slice(2)) }
  }
  if (key.startsWith('g:')) {
    return { kind: 'localGeotiff', localId: key.slice(2) }
  }
  throw new Error(`Invalid stack key: ${key}`)
}

export function projectOrderFromStack(slots: MapStackSlot[]): number[] {
  return slots.filter((s): s is Extract<MapStackSlot, { kind: 'project' }> => s.kind === 'project').map(s => s.layerId)
}

/** Same layer ids ignoring order (allows SetLayerOrder when ReactSortable emits a transient bad list during init/sync). */
export function isSameLayerIdMultiset(canonical: readonly number[], candidate: readonly number[]): boolean {
  if (canonical.length !== candidate.length) {
    return false
  }
  const a = [...canonical].sort((x, y) => x - y)
  const b = [...candidate].sort((x, y) => x - y)
  return a.every((v, i) => v === b[i])
}

/**
 * Keep sidebar/map stack aligned with reducer `project.allLayers`:
 * duplicates removed project rows, append new IDs (matching AddLayer), reorder project rows while preserving locals in place.
 */
export function reconcileMapStackWithProject(
  slots: MapStackSlot[],
  allLayers: readonly number[],
): MapStackSlot[] {
  const valid = new Set(allLayers)
  const next = slots.filter(s => s.kind === 'localGeotiff' || valid.has(s.layerId))

  const projIdsOrdered = projectOrderFromStack(next)
  const goal = [...allLayers]
  const addedIds = goal.filter(id => !projIdsOrdered.includes(id))
  let withAdded: MapStackSlot[] = [...next]
  for (const layerId of addedIds) {
    withAdded.push({ kind: 'project', layerId })
  }

  let gi = 0
  return withAdded.map(s => {
    if (s.kind === 'localGeotiff') {
      return s
    }
    return { kind: 'project', layerId: goal[gi++] }
  })
}
